// Story 7.1 (ADO-904), AC1 — three-level Department › Category › Sub-Category
// browse navigation (the Figma "Mega Menu - Browse", node 576:4187).
//
// A three-column drill-down: "All Departments" is always shown; selecting a
// Department loads its Categories into the middle column; selecting a Category
// loads its Sub-Categories into the right column. Selecting any node also
// notifies the parent (`onSelectNode`) so the product listing updates to that
// node — "products within that department/category are displayed" (epics.md 7.1).
//
// Scope: navigation only. The header/search/cart chrome around the mega-menu in
// Figma belongs to other stories; this renders the drill-down panel.

import { useCallback, useEffect, useRef } from 'react';
import { Icon } from '../../design-system';
import type { CategoryNode } from '../../api/browse.client';
import { NoEntryPrompt } from '../../ordering/NoEntryPrompt';
import { formatCategoryName } from './categoryDisplay';
import styles from './BrowseNav.module.css';
import { type ColumnState, useCategoryChildren } from './useCategoryChildren';

// Story 7-FU-D (ADO-1217), AC1 — hover-intent delay. A pointer/focus sweep down a
// column fires `onMouseEnter`/`onFocus` on every traversed row; without a delay
// each one drives an `onExpandNode` → a `fetchChildren` (the reveal changes the
// parent's active drill-state, the fetch effect's dep) and flashes a "Loading…".
// Debouncing the reveal so only the row the pointer SETTLES on triggers it
// coalesces the sweep to one fetch. Kept short so a deliberate dwell still feels
// instant; true "feel" is a Playwright/manual concern (happy-dom has no layout).
const HOVER_INTENT_MS = 140;

export interface BrowseNavProps {
  /** The active Department (its Categories fill the middle column); null = none drilled. */
  readonly activeDepartmentId: string | null;
  /** The active Category (its Sub-Categories fill the right column); null = none drilled. */
  readonly activeCategoryId: string | null;
  /** The currently selected node (highlights the trail); null before any selection. */
  readonly selectedNodeId: string | null;
  /** Called when the user selects a node at any level; the parent reconciles the drill-state. */
  readonly onSelectNode: (node: CategoryNode) => void;
  /**
   * Called when a Department/Category row is hovered or focused — reveals the
   * next column without selecting (loading no products). The parent updates the
   * active drill-state only. Mirrors the prototype mega-menu's hover drill.
   */
  readonly onExpandNode: (node: CategoryNode) => void;
}

function NavColumn({
  column,
  activeId,
  selectedNodeId,
  onSelect,
  onExpand,
}: {
  column: ColumnState;
  activeId: string | null;
  selectedNodeId: string | null;
  onSelect: (node: CategoryNode) => void;
  /**
   * Hover/focus a row to reveal the next column WITHOUT selecting it (mega-menu
   * behaviour — mirrors the prototype's `onMouseEnter`/`onFocus` drill). Omitted
   * for the leaf (Sub-Category) column, where there is nothing further to reveal.
   */
  onExpand?: (node: CategoryNode) => void;
}) {
  // Hover-intent timer (AC1): a reveal is scheduled on enter/focus and fires only
  // once the pointer/focus DWELLS on a row; a subsequent enter/focus supersedes
  // it, and a leave/blur or a committed click cancels it, so a fast sweep resolves
  // to a single reveal. Mirrors the timer-cleanup discipline in the add buttons
  // (Task 5): the timer is cleared on unmount so it never fires into a dead column.
  const intentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelIntent = useCallback(() => {
    if (intentTimer.current !== null) {
      clearTimeout(intentTimer.current);
      intentTimer.current = null;
    }
  }, []);
  const scheduleExpand = useCallback(
    (node: CategoryNode) => {
      cancelIntent();
      intentTimer.current = setTimeout(() => {
        intentTimer.current = null;
        onExpand?.(node);
      }, HOVER_INTENT_MS);
    },
    [cancelIntent, onExpand],
  );
  useEffect(() => cancelIntent, [cancelIntent]);

  return (
    <div className={styles.column}>
      <p className={styles.columnLabel}>{column.label}</p>
      {column.noEntry && <NoEntryPrompt className={styles.hint} />}
      {column.loading && (
        <p className={styles.hint} role="status">
          Loading…
        </p>
      )}
      {column.error && (
        <p className={styles.hint} role="alert">
          {column.error}
        </p>
      )}
      {!column.loading && !column.error && !column.noEntry && column.nodes.length === 0 && (
        <p className={styles.hint}>Nothing here.</p>
      )}
      <ul className={styles.list}>
        {column.nodes.map((node) => {
          // `aria-current` marks the COMMITTED selection only (a single node), so a
          // merely-hovered/revealed row is never announced "current" and no column
          // ever carries two `aria-current` rows. The revealed drill-trail highlight
          // is a separate, non-ARIA visual concern (`.isRevealed`).
          const isCurrent = node.id === selectedNodeId;
          const isRevealed = node.id === activeId || isCurrent;
          // Hover/focus reveals the next column — but only when there IS one. Gating
          // on `hasChildren` stops a childless Department/Category from opening an
          // empty column; the chevron still renders on every row (prototype match).
          const canExpand = onExpand !== undefined && node.hasChildren;
          return (
            <li key={node.id}>
              <button
                type="button"
                className={isRevealed ? `${styles.row} ${styles.isRevealed}` : styles.row}
                aria-current={isCurrent ? 'true' : undefined}
                // The committed click path stays IMMEDIATE (AC1) — and cancels any
                // pending hover intent so a click is never trailed by a redundant
                // reveal of the same row.
                onClick={() => {
                  cancelIntent();
                  onSelect(node);
                }}
                onMouseEnter={canExpand ? () => scheduleExpand(node) : undefined}
                onMouseLeave={canExpand ? cancelIntent : undefined}
                onFocus={canExpand ? () => scheduleExpand(node) : undefined}
                onBlur={canExpand ? cancelIntent : undefined}
              >
                <span className={styles.rowLabel}>{formatCategoryName(node.name)}</span>
                {/* Chevron on every row (matches the prototype) — a drill/go
                    affordance at all three levels, muted like the filter rows. */}
                <Icon name="caretRight" size={16} className={styles.chevron} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function BrowseNav({
  activeDepartmentId,
  activeCategoryId,
  selectedNodeId,
  onSelectNode,
  onExpandNode,
}: BrowseNavProps) {
  const departments = useCategoryChildren(null, 'All Departments', true);
  const categories = useCategoryChildren(
    activeDepartmentId,
    'Category',
    activeDepartmentId !== null,
  );
  const subCategories = useCategoryChildren(
    activeCategoryId,
    'Sub-Category',
    activeCategoryId !== null,
  );

  return (
    <nav className={styles.nav} aria-label="Browse departments">
      <NavColumn
        column={departments}
        activeId={activeDepartmentId}
        selectedNodeId={selectedNodeId}
        onSelect={onSelectNode}
        onExpand={onExpandNode}
      />
      {activeDepartmentId !== null && (
        <NavColumn
          column={categories}
          activeId={activeCategoryId}
          selectedNodeId={selectedNodeId}
          onSelect={onSelectNode}
          onExpand={onExpandNode}
        />
      )}
      {activeCategoryId !== null && (
        <NavColumn
          column={subCategories}
          activeId={null}
          selectedNodeId={selectedNodeId}
          onSelect={onSelectNode}
        />
      )}
    </nav>
  );
}
